import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert'; // Import
import DataTable from 'react-data-table-component';
import { NavLink } from 'react-router-dom';
import Tooltip from 'react-simple-tooltip';

import { useGlobalContext } from '../../AppContext';
import { WhalesTableDataProps } from '../../constants/interfaces';
import {
  add_favorite_token,
  add_favorite_wallet,
  remove_favorite_token,
  remove_favorite_wallet,
  remove_favorite_whale,
} from '../../utils/apiOperations';
import { networkIcons } from '../../utils/constants';
import { BigMenu } from '../../components/BigMenu';
import { ConfirmAlert } from '../../components/ConfirmAlert';

import './Whales.scss';

export const Whales = () => {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = document.body.classList.contains('dark') ? 'light' : 'dark';
      setTheme(newTheme);
    };

    handleThemeChange();

    const observer = new MutationObserver(handleThemeChange);
    const config = { attributes: true, attributeFilter: ['class'] };
    observer.observe(document.body, config);

    return () => {
      observer.disconnect();
    };
  }, []);

  const {
    config,
    whales,
    isMobile,
    favorites,
    setFavorites,
    setCustomSearch,
    setAddress,
    setPreparing,
    setWhales,
    tokenDetails,
  } = useGlobalContext();

  const [showTable, setShowTable] = useState<JSX.Element>(<></>);
  const [tableData, setTableData] = useState<WhalesTableDataProps[]>([]);

  const renderShowTable = (data: any): void => {
    setShowTable(
      <DataTable
        noDataComponent={
          <div className="data-table-no-record">
            There are no records to display
            <hr />
            <div>
              <small>
                You can follow whale accounts here. Find some and add it to your whales list.
              </small>
            </div>
          </div>
        }
        columns={columns}
        data={data}
        expandableRows={true}
        expandableRowsComponent={ExpandedComponent}
        expandOnRowClicked={false}
        expandOnRowDoubleClicked={false}
        expandableRowsHideExpander={false}
      />,
    );
  };

  const columns = [
    {
      name: 'Token Name',
      selector: (row: WhalesTableDataProps) => {
        return (
          <div className="token-icon-holder">
            <img src={row.icon} className="expanded-icon" alt="" />
            {row.tokenName}
          </div>
        );
      },
      sortable: true,
      width: '300px',
    },
    {
      name: 'Holders',
      selector: (row: WhalesTableDataProps) => row.holders,
      sortable: true,
      width: '100px',
    },
    {
      name: '',
      selector: () => '',
    },
    {
      name: '',
      selector: (row: WhalesTableDataProps) => row.favorite,
      cell: (row: WhalesTableDataProps) => (
        <div className="table-actions">
          <Tooltip
            placement="left"
            className="tooltip"
            border="#ff6aff"
            background="var(--color-background-main)"
            color="var(--color-text-main)"
            content={`${favorites.tokens[row.coingeckoid] ? 'Remove from' : 'Add to'} Favorites`}
          >
            <img
              onClick={() => {
                if (favorites.tokens[row.coingeckoid]) {
                  remove_favorite_token(favorites.tokens[row.coingeckoid], setFavorites);
                } else {
                  add_favorite_token(row.coingeckoid, setFavorites);
                }
              }}
              className={`${favorites.tokens && favorites.tokens[row.coingeckoid] ? 'active' : ''}`}
              src="/icons/star2.svg"
              alt=""
            />
          </Tooltip>
          <Tooltip
            placement="left"
            className="tooltip"
            border="#ff6aff"
            background="var(--color-background-main)"
            color="var(--color-text-main)"
            content={`Remove from Whales`}
          >
            <img
              onClick={() =>
                confirmAlert({
                  closeOnClickOutside: false,
                  customUI: ({ onClose }) => {
                    return (
                      <ConfirmAlert title="Are you sure?" onClose={onClose}>
                        <div className="confirm-button-flex">
                          <BigButton type="light" size="small" onClick={onClose} name="No" />
                          <BigButton
                            type="dark"
                            size="small"
                            onClick={() => {
                              remove_favorite_whale(row.id, setWhales);
                              onClose();
                            }}
                            name="Yes, Delete it!"
                          />
                        </div>
                      </ConfirmAlert>
                    );
                  },
                })
              }
              src="/icons/trash.svg"
              alt=""
            />
          </Tooltip>
          <Tooltip
            placement="left"
            className="tooltip"
            border="#ff6aff"
            content={`Set a Reminder`}
            background="var(--color-background-main)"
            color="var(--color-text-main)"
          >
            <NavLink
              to={{
                pathname: '/reminders',
              }}
            >
              <img src="/icons/reminder.svg" alt="" />
            </NavLink>
          </Tooltip>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const initialTableData: WhalesTableDataProps[] = new Array();

    if (config) {
      setTableData(initialTableData);
      setWhales(false);
    } else {
      setTableData(initialTableData);
    }
  }, [config]);

  useEffect(() => {
    renderShowTable(tableData);
    if (isMobile && document.getElementsByClassName('rdt_Table')[0]) {
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 50,
          behavior: 'smooth',
        });
      }, 200);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 60,
          behavior: 'smooth',
        });
      }, 210);
      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 70,
          behavior: 'smooth',
        });
      }, 220);

      setTimeout(() => {
        document.getElementsByClassName('rdt_Table')[0].scroll({
          left: 0,
          behavior: 'smooth',
        });
      }, 800);
    }
  }, [tableData]);

  useEffect(() => {
    const tmpData: WhalesTableDataProps[] = new Array();
    console.log(whales);
    Object.keys(whales).forEach((item: any) => {
      const tmp = Object.values(tokenDetails).filter((i: any) => {
        return i.coingeckoid === item;
      });

      if (tmp.length > 0) {
        tmpData.push({
          id: whales[item]['id'],
          coingeckoid: item,
          tokenName: whales[item]['name'],
          icon: tmp[0].logoURI ? tmp[0].logoURI : networkIcons[item],
          contract: item,
          holders: tmp[0].holders,
          holdersDetails: whales[item]['holdersData'],
          favorite: true,
        });
      }
    });

    setTableData(tmpData);
  }, [favorites, whales]);

  const addressChecker = (arr: any, address: string) => {
    return arr.some((el: any) => {
      return el.address === address;
    });
  };

  const walletIdFinder = (arr: any, address: string) => {
    if (
      arr.some((el: any) => {
        return el.address === address;
      })
    ) {
      return arr.filter((el: any) => {
        return el.address === address;
      })[0]['id'];
    } else {
      return 0;
    }
  };

  const ExpandedComponent = (data: any) => {
    return (
      <>
        {data.data.holdersDetails && (
          <div className="rdt_TableHeadRow">
            <div className="rdt_TableCol" style={{ width: '52px' }}></div>
            <div className="rdt_TableCol" style={{ width: '600px' }}>
              Title
            </div>
            <div className="rdt_TableCol" style={{ width: '100px' }}>
              Ratio (%)
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}>
              Quantity
            </div>
            <div className="rdt_TableCol" style={{ width: '120px' }}></div>
          </div>
        )}
        {data.data.holdersDetails &&
          data.data.holdersDetails.map((item: any, index: number) => {
            if (item.t === item.c)
              return (
                <div key={index} role="row" className="rdt_TableRow expandedRow">
                  <div className="rdt_TableCell disabled" style={{ width: '52px' }}>
                    {' '}
                    <img src={data.data.icon} className="expanded-icon" alt="" />
                  </div>
                  <div className="rdt_TableCell" style={{ width: '600px' }}>
                    <div className="ellipsis">
                      <strong>{item.t}</strong>
                      {item.t !== item.c ? (
                        <>
                          <br />
                          {item.c}
                        </>
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                  <div className="rdt_TableCell" style={{ width: '100px' }}>
                    <div className="ellipsis">{`${parseFloat(
                      parseFloat(item.p).toFixed(2).toString(),
                    )}%`}</div>
                  </div>
                  <div className="rdt_TableCell" style={{ width: '120px' }}>
                    <div className="ellipsis">
                      {parseFloat(item['q'])
                        .toFixed(2)
                        .toString()
                        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}
                    </div>
                  </div>
                  <div className="rdt_TableCell" style={{ width: '120px' }}>
                    <div className="table-actions">
                      <Tooltip
                        placement="left"
                        className="tooltip"
                        border="#ff6aff"
                        background="var(--color-background-main)"
                        color="var(--color-text-main)"
                        content={`See through this wallet`}
                      >
                        <NavLink
                          onClick={() => {
                            setCustomSearch(true);
                            setAddress(item.c);
                            setPreparing(true);
                          }}
                          to={{
                            pathname: '/',
                          }}
                        >
                          <img src={`/icons/eye-${theme}.svg`} alt="" />
                        </NavLink>
                      </Tooltip>
                      <Tooltip
                        placement="left"
                        className="tooltip"
                        border="#ff6aff"
                        background="var(--color-background-main)"
                        color="var(--color-text-main)"
                        content={`${
                          addressChecker(Object.values(favorites.wallets), item.c)
                            ? 'Remove from'
                            : 'Add to'
                        } Favorites`}
                      >
                        <img
                          onClick={() => {
                            if (addressChecker(Object.values(favorites.wallets), item.c)) {
                              remove_favorite_wallet(
                                walletIdFinder(Object.values(favorites.wallets), item.c),
                                setFavorites,
                              );
                            } else {
                              add_favorite_wallet(item.c, item.t ? item.t : item.c, setFavorites);
                            }
                          }}
                          className={`${
                            favorites.wallets &&
                            addressChecker(Object.values(favorites.wallets), item.c)
                              ? 'active'
                              : ''
                          }`}
                          src="/icons/star2.svg"
                          alt=""
                        />
                      </Tooltip>
                      <Tooltip
                        placement="left"
                        className="tooltip"
                        border="#ff6aff"
                        background="var(--color-background-main)"
                        color="var(--color-text-main)"
                        content={`Set a Reminder`}
                      >
                        <NavLink
                          to={{
                            pathname: '/reminders',
                          }}
                        >
                          <img src="/icons/reminder.svg" alt="" />
                        </NavLink>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              );
          })}
        {!data.data.holdersDetails && (
          <div role="row" className="rdt_TableRow expandedRow">
            <div className="rdt_TableCell disabled"></div>
            <div className="rdt_TableCell">
              There is no data at the moment, please try again later.
            </div>
            <div className="rdt_TableCell"></div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="whales">
      <BigMenu horizontal={true} />
      {config && (
        <div className={`custom-table-${theme} mt-5 padding-left`}>
          <div className="table-border-top" />
          <div>{showTable}</div>
          <div className="table-border-bottom" />
        </div>
      )}
    </div>
  );
};
